/**
 * PromotionTermsPage — dedicated route hosting the dynamic
 * PromotionTermsSection. Renders a full page shell only when at least
 * one campaign is active; otherwise the terms section itself returns
 * null and the page shows a minimal "no promotions running" note
 * with a back-to-home link so the URL still resolves cleanly.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PromotionTermsSection from "../components/PromotionTermsSection";

export default function PromotionTermsPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm"
          data-testid="promo-terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Saturn Styling
        </Link>
      </div>

      <PromotionTermsSection />

      {/* Fallback for when the terms section renders null (no active
          promos). Rendered as a sibling so the section's null-return
          stays correct on inline embeds (e.g. footer link jumps). */}
      <NoPromoFallback />
    </div>
  );
}

// Only visible when the terms section itself renders nothing. We
// detect that by looking for its anchor id in the DOM after mount —
// cheaper than a duplicated fetch. Behaves as a static message so
// crawlers still see something meaningful on the URL.
const NoPromoFallback = () => {
  const [empty, setEmpty] = React.useState(false);
  React.useEffect(() => {
    // Small delay so the section has a chance to render.
    const t = setTimeout(() => {
      setEmpty(!document.getElementById("promotion-terms"));
    }, 400);
    return () => clearTimeout(t);
  }, []);
  if (!empty) return null;
  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center"
      data-testid="promo-terms-empty"
    >
      <h1 className="text-2xl font-bold text-white mb-3">No promotions running</h1>
      <p className="text-slate-400 text-sm">
        There are no active Saturn Styling promotions right now. When one is live,
        the full terms will appear here automatically.
      </p>
    </section>
  );
};
